import { Trans, useLingui } from "@lingui/react/macro";
import { Plus, Trash } from "lucide-react";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";

import {
  apiDeleteSchedulesUuid,
  apiPostSchedules,
  apiPutSchedulesUuid,
  PatientScheduleResponse,
  ScheduleTypes,
} from "../../api";
import { handleError } from "../../app/errors";
import { Button } from "../../components/ui-kit/base/button";
import { Button as ButtonOld } from "../../components/ui-kit/button";
import { Dialog } from "../../components/ui-kit/dialog";
import { NumberInput } from "../../components/ui-kit/fields/number-input";
import { TagsInput } from "../../components/ui-kit/fields/tags-input";
import { FormRowEditable } from "../../components/ui-kit/form/form-row-editable";
import { Patient } from "./patient";
import { formatTimeDisplay, scheduleToString } from "./schedule-utils";

export const ScheduleFormRow = observer(function ScheduleFormRow(props: {
  patient: Patient;
}) {
  const { t } = useLingui();
  const [dialogOpen, setDialogOpen] = useState(false);

  const schedules = useMemo(
    () => props.patient.schedules?.paginatedData ?? [],
    [props.patient.schedules?.paginatedData],
  );

  return (
    <>
      <FormRowEditable
        label={t`Schedule`}
        viewMode={
          <>
            {schedules.length === 0 && (
              <span className="text-muted-foreground">
                <Trans>No schedule</Trans>
              </span>
            )}
            {schedules.map((schedule) => (
              <div key={schedule.uuid}>{scheduleToString(schedule)}</div>
            ))}
          </>
        }
        onEditClick={() => setDialogOpen(true)}
      />

      {dialogOpen && (
        <ScheduleEditDialog
          patient={props.patient}
          schedules={schedules}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
});

interface EditedSchedule {
  uuid: string | undefined;
  schedule_times: string[];
  schedule_frequency_regular_repeat_every: number | null | undefined;
}

const ScheduleEditDialog = observer(function ScheduleEditDialog(props: {
  patient: Patient;
  schedules: PatientScheduleResponse[];
  onClose: () => void;
}) {
  const [editedSchedules] = useState(() =>
    observable(
      props.schedules.map(
        (schedule): EditedSchedule => ({
          uuid: schedule.uuid,
          schedule_times: [...(schedule.schedule_times ?? [])],
          schedule_frequency_regular_repeat_every:
            schedule.schedule_frequency_regular_repeat_every,
        }),
      ),
    ),
  );

  const [pending, setPending] = useState(false);
  const { t } = useLingui();

  const handleSave = async () => {
    setPending(true);
    try {
      for (const schedule of editedSchedules) {
        const body = {
          schedule_times: normalizeScheduleTimes(schedule.schedule_times),
          schedule_frequency_regular_repeat_every:
            schedule.schedule_frequency_regular_repeat_every,
        };

        if (schedule.uuid) {
          await apiPutSchedulesUuid({
            path: { uuid: schedule.uuid },
            body,
          });
        } else {
          await apiPostSchedules({
            body: {
              patient_id: props.patient.patientId,
              schedule_type: ScheduleTypes.full_measurement,
              specific_schedule_data_name: ["full measurement"],
              ...body,
            },
          });
        }
      }

      const deletedUuids = props.schedules
        .filter((s) => !editedSchedules.find((s2) => s2.uuid === s.uuid))
        .map((s) => s.uuid);

      for (const uuid of deletedUuids) {
        await apiDeleteSchedulesUuid({ path: { uuid } });
      }

      await props.patient.fetch();
      props.onClose();
    } catch (error) {
      await handleError(error);
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      className="w-[400px]"
    >
      <h2 className="text-lg font-semibold mb-4">
        <Trans>Edit Schedule</Trans>
      </h2>

      <div className="flex flex-col gap-3 mb-4">
        {editedSchedules.map((schedule, index) => (
          <div
            key={index}
            className="flex items-start gap-3 rounded-md border border-border bg-muted p-3"
          >
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  <Trans>Every</Trans>
                </span>
                <NumberInput
                  attr="schedule_frequency_regular_repeat_every"
                  model={schedule}
                  width={60}
                  min={1}
                />
                <span className="text-sm text-muted-foreground">
                  <Trans>days</Trans>
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">
                  <Trans>Times:</Trans>
                </span>
                <TagsInput
                  attr="schedule_times"
                  model={schedule}
                  placeholder={t`e.g. 8:00, 14:30`}
                  validate={validateTime}
                  formatDisplay={formatTimeDisplay}
                />
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                editedSchedules.splice(index, 1);
              }}
            >
              <Trash className="size-4" />
            </Button>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            editedSchedules.push({
              uuid: undefined,
              schedule_times: [],
              schedule_frequency_regular_repeat_every: 1,
            });
          }}
          className="w-fit"
        >
          <Plus className="size-4" />
          <Trans>Add schedule</Trans>
        </Button>
      </div>

      <div className="flex gap-2 justify-end">
        <ButtonOld onClick={props.onClose}>
          <Trans>Cancel</Trans>
        </ButtonOld>
        <ButtonOld onClick={handleSave} pending={pending}>
          <Trans>Save</Trans>
        </ButtonOld>
      </div>
    </Dialog>
  );
});

function normalizeScheduleTimes(times: string[]): string[] {
  return times.map((time) => {
    const [hoursStr, minutesStr = "00", secondsStr = "00"] = time.split(":");
    return `${hoursStr.padStart(2, "0").trim()}:${minutesStr.trim()}:${secondsStr.trim()}`;
  });
}

/** Validate and format time input. Returns HH:MM format or null if invalid. */
function validateTime(value: string): string | null {
  const match = value.match(/^(\d{1,2}):?(\d{2})?$/);
  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}
